import './Layout.scss';

import { FloatButton } from 'antd';
import { jwtDecode } from 'jwt-decode';
import { type FC, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams
} from 'react-router';

import { DictTypes, pathServiceDeskIcon } from '@/constants';
import { clearLocalStorageHelper } from '@/services/helpers/clearLocalStorage';
import { getUserToken } from '@/services/user-hash';
import { useAppDispatch, useAppSelector } from '@/store';
import { setIsAuth } from '@/store/auth.slice';
import {
  getOptimizedDictionaries,
  getProductInsurance,
  getProductsDictionary,
  setUpdateDicts
} from '@/store/dict.slice';
import { startBadgeLeads } from '@/store/leads.slice';
import type { TLoadingActionType } from '@/store/loading.slice';
import { startNotification } from '@/store/notifications.slice';
import { startBadgeSecurity } from '@/store/security.slice';
import {
  startAdminBadgeNewServiceDesk,
  startAdminBadgeReworkServiceDesk,
  startUserBadgeServiceDesk
} from '@/store/serviceDesk.slice';
import {
  getUserMenu,
  setDealer,
  setJwtDecodedInfo
} from '@/store/userInfo.slice';
import type { DecodedJWTToken } from '@/types/user';

import AdminMobileMenu from '../AdminPanel/NavBar/admin-mobile-menu';
import { ROUTES } from '../Shared/routes/routes';
import { AllurLoader, UpdateBanner } from '../Shared/ui';
import ErrorTemplate from '../Shared/widgets/errors/error-template';
import NotificationsSideBar from './Notifications/NotificationsSideBar';
import { Header, Navbar } from './widgets';
import MobileNavbar from './widgets/mobile-menu';

export const Layout: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dicts, updatingDicts } = useAppSelector((state) => state.dict);
  const {
    fullscreenCount,
    fullscreenBlurCount,
    layoutCount,
    layoutBlurCount,
    loadingsWithText
  } = useAppSelector((state) => state.loading);

  const {
    dealer,
    menu,
    userInfo,
    userAccesses: {
      security: { hasGeneralAccessToSecurity },
      global: { isAdmin }
    }
  } = useAppSelector((state) => state.userInfo);
  const { isUserAuth } = useAppSelector((state) => state.auth);
  const { newMessagesUserBadge, newMessageCountRequestMap } = useAppSelector(
    (state) => state.serviceDesk
  );
  const dispatch = useAppDispatch();

  const dealersDict = dicts?.[DictTypes.Dealers];
  const dealerCode = localStorage.getItem('dealer');
  const isAdminPage = location.pathname.startsWith('/adminPage');
  const isServiceDeskPage = location.pathname.startsWith('/service-desk');

  const [searchParams] = useSearchParams();

  const showServiceDeskFab =
    !isAdmin &&
    !isServiceDeskPage &&
    !(
      location.pathname === '/personal' &&
      searchParams.get('tab') === 'service-desk'
    );

  const newMessagesUserBadgeUpdated: number = useMemo(() => {
    if (!userInfo) return 0;
    return (
      Object.values(newMessageCountRequestMap).reduce(
        (total, count) => (total ?? 0) + (count ?? 0),
        0
      ) ?? 0
    );
  }, [newMessageCountRequestMap, userInfo]);

  const nearestLoadingWithText = useMemo(() => {
    const result: Record<TLoadingActionType, string> = {
      fullscreen: '',
      fullscreenBlur: '',
      layout: '',
      layoutBlur: ''
    };

    for (let i = loadingsWithText.length - 1; i >= 0; i--) {
      const loading = loadingsWithText[i];
      result[loading.type] = loading.text;
    }

    return result;
  }, [loadingsWithText]);

  const hasFullscreenLoading: boolean =
    fullscreenCount > 0 || nearestLoadingWithText.fullscreen !== '';

  const hasFullscreenLoadingBlur: boolean =
    fullscreenBlurCount > 0 || nearestLoadingWithText.fullscreenBlur !== '';

  const hasFullscreenLoadingTotal: boolean =
    hasFullscreenLoading || hasFullscreenLoadingBlur;

  const hasLayoutLoading: boolean =
    layoutCount > 0 || nearestLoadingWithText.layout !== '';

  const hasLayoutLoadingBlur: boolean =
    layoutBlurCount > 0 || nearestLoadingWithText.layoutBlur !== '';

  const hasLayoutLoadingTotal: boolean =
    !hasFullscreenLoadingTotal && (hasLayoutLoading || hasLayoutLoadingBlur);

  useEffect(() => {
    if (isUserAuth && userInfo) {
      dispatch(startNotification());
      dispatch(startBadgeLeads());
    }
  }, [isUserAuth, userInfo]);

  useEffect(() => {
    if (isUserAuth && userInfo) {
      if (isAdmin) {
        dispatch(startAdminBadgeNewServiceDesk());
        dispatch(startAdminBadgeReworkServiceDesk());
      } else {
        dispatch(startUserBadgeServiceDesk(userInfo.uuid));
      }
    }
  }, [isUserAuth, userInfo, isAdmin]);

  useEffect(() => {
    if (isUserAuth && userInfo && hasGeneralAccessToSecurity) {
      dispatch(startBadgeSecurity());
    }
  }, [isUserAuth, userInfo, hasGeneralAccessToSecurity]);

  useEffect(() => {
    if (dealerCode && dealersDict) {
      const dealerFind =
        dealersDict.getBy.code[dealerCode] ||
        dealersDict.content.find(
          (dealer) => Number(dealer.code) === Number(dealerCode)
        );
      if (dealerFind) {
        const selectedDealer = {
          value: dealerFind.uuid,
          label: dealerFind.name,
          code: Number(dealerFind.code)
        };
        dispatch(setDealer(selectedDealer));
      }
    }
  }, [dealersDict, dealersDict?.content, dealerCode]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isUserAuth && dealerCode === null) {
        navigate(ROUTES.login);
        dispatch(setIsAuth(false));
        clearLocalStorageHelper();
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [isUserAuth, dealerCode]);

  useEffect(() => {
    if (dealer?.value && menu.length === 0 && isUserAuth)
      dispatch(getUserMenu(dealer.value));
  }, [dispatch, dealer?.value]);

  useEffect(() => {
    (async () => {
      const accessToken = getUserToken().accessToken;
      if (accessToken) {
        await dispatch(getOptimizedDictionaries());
        const data = jwtDecode<DecodedJWTToken>(accessToken || '');
        dispatch(setJwtDecodedInfo(data));
        await dispatch(getProductsDictionary());
        await dispatch(getProductInsurance());
      }
    })();
  }, []);

  useEffect(() => {
    if (updatingDicts) {
      dispatch(
        getOptimizedDictionaries({
          updatingDicts: updatingDicts,
          cacheConfig: { ignoreCache: true }
        })
      );
      dispatch(setUpdateDicts(undefined));
    }
  }, [updatingDicts]);

  return (
    <div className="layout">
      {hasFullscreenLoadingTotal &&
        (hasFullscreenLoadingBlur ? (
          <AllurLoader
            fullScreen
            blurMode
            text={nearestLoadingWithText.fullscreenBlur}
          />
        ) : (
          hasFullscreenLoading && (
            <AllurLoader fullScreen text={nearestLoadingWithText.fullscreen} />
          )
        ))}

      <Navbar />

      <div className="layout__wrapper">
        <Header />

        <main
          className={`layout__main ${hasLayoutLoadingTotal ? 'layout__main--loading' : ''}`}
        >
          {hasLayoutLoadingTotal &&
            (hasLayoutLoadingBlur ? (
              <AllurLoader
                layout
                blurMode
                text={nearestLoadingWithText.layoutBlur}
              />
            ) : (
              hasLayoutLoading && (
                <AllurLoader layout text={nearestLoadingWithText.layout} />
              )
            ))}

          <ErrorBoundary
            onError={console.error}
            fallback={<ErrorTemplate type="oops" />}
          >
            <Outlet key={location.pathname} />
          </ErrorBoundary>
        </main>

        <UpdateBanner />

        {isAdminPage ? <AdminMobileMenu /> : <MobileNavbar />}
      </div>

      <NotificationsSideBar />
      {showServiceDeskFab && (
        <FloatButton
          badge={{ count: newMessagesUserBadge || newMessagesUserBadgeUpdated }}
          tooltip={{
            title: 'Тех. поддержка',
            placement: 'topLeft'
          }}
          className="layout__service-desk-fab"
          onClick={() => {
            window.open(ROUTES.personal + '?tab=service-desk', '_blank');
          }}
          icon={
            <img
              src={pathServiceDeskIcon}
              alt=""
              className="layout__service-desk-icon"
            />
          }
        />
      )}
    </div>
  );
};
